import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useHeader } from '../../context/HeaderContext';
import { LoadingIndicator } from '../shared/LoadingIndicator';
import { useAirtableQuery } from '../../hooks/useAirtableQuery';
import { SectionHeader } from '../layout/SectionHeader';
import { resolveColor } from '../../utils/colorUtils';
import { useSection } from '../../hooks/useSection';
import { reviewClient } from '../../lib/review/reviewClient';
import { analyticsService } from '../../lib/analytics/analyticsService';
import type { SupportedLanguages } from '../../i18n';
import ReviewForm from './ReviewForm';

const ReviewSection: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { setTitle } = useHeader();
  const section = useSection();
  const currentLanguage = i18n.language as SupportedLanguages;

  // Data fetching hooks
  const { data: config, isLoading: configLoading } = useAirtableQuery.useConfig();
  const { data: translations, isLoading: translationsLoading } = useAirtableQuery.useTranslations(i18n.language as SupportedLanguages);

  // Fetch review translations
  const { data: reviewTranslations, isLoading: reviewTranslationsLoading } = useQuery({
    queryKey: ['review-translations', section?.id],
    queryFn: async () => {
      if (!section?.id) {
        throw new Error('Section ID not found');
      }
      return reviewClient.getReviewTranslations(section.id);
    },
    enabled: !!section?.id,
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000
  });

  // Fetch review links
  const { data: reviewLinks, isLoading: reviewLinksLoading } = useQuery({
    queryKey: ['review-links', section?.id],
    queryFn: async () => {
      if (!section?.id) {
        throw new Error('Section ID not found');
      }
      return reviewClient.getReviewLinks(section.id);
    },
    enabled: !!section?.id,
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000
  });

  // Track section view
  useEffect(() => {
    if (section?.id) {
      analyticsService.trackSectionView(
        section.id,
        'review',
        'form',
        {
          section_name: section.names.fr || 'Unknown Section'
        }
      );
    }
  }, [section?.id]);

  // Set page title
  useEffect(() => {
    if (section?.names) {
      const name = section.names[i18n.language as SupportedLanguages] || section.names.fr;
      setTitle(name);
    }
  }, [section, i18n.language, setTitle]);

  // Handle back button click
  const handleBackClick = () => {
    navigate('/');
  };

  // Get translated text from review translations
  const getReviewTranslation = (): string => {
    if (!reviewTranslations || reviewTranslations.length === 0) {
      return '';
    }

    const translation = reviewTranslations[0];
    return translation[currentLanguage] || translation.fr || '';
  };

  // Loading state
  if (configLoading || translationsLoading || reviewTranslationsLoading || reviewLinksLoading) {
    return <LoadingIndicator />;
  }

  // Error state
  if (!config || !translations || !section || !reviewLinks) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-red-600">
        <h2 className="text-xl font-semibold mb-2">Une erreur est survenue</h2>
        <p className="text-center mb-4">Erreur lors du chargement des données</p>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Retour à l'accueil
        </button>
      </div>
    );
  }

  // No review links available
  if (reviewLinks.length === 0) {
    return (
      <div 
        className="min-h-screen"
        style={{ 
          backgroundColor: resolveColor(config['sectionBack-color'], config, '#f9fafb')
        }}
      >
        <SectionHeader
          config={config}
          title={section.names[currentLanguage] || section.names.fr}
          onBackClick={handleBackClick}
          translations={translations}
        />
        
        <div className="max-w-lg mx-auto px-4 py-8 text-center">
          <p className="text-gray-600">
            {translations?.review_no_platforms?.[currentLanguage] || 
             translations?.review_no_platforms?.fr || 
             'Aucune plateforme d\'avis n\'est configurée pour cet établissement.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen"
      style={{ 
        backgroundColor: resolveColor(config['sectionBack-color'], config, '#f9fafb')
      }}
    >
      <SectionHeader
        config={config}
        title={section.names[currentLanguage] || section.names.fr}
        onBackClick={handleBackClick}
        translations={translations}
      />

      <ReviewForm
        config={config}
        translations={translations}
        reviewTranslation={getReviewTranslation()}
        reviewLinks={reviewLinks}
        sectionId={section.id}
      />
    </div>
  );
};

export default ReviewSection;
